import { isIsolatedCloud, isolationContextId } from '@atlassian/atl-context';

import { type envOverrides, envType, perimeterType } from './analyticsWebTypes';

const PROD_HOST = 'as.atlassian.com/api/v1';
const STARGATE_PROD_HOST = 'api-private.atlassian.com/gasv3/api/v1';
const STAGING_HOST = 'as.staging.atl-paas.net/api/v1';
const STARGATE_STAGING_HOST = 'api-private.stg.atlassian.com/gasv3/api/v1';
const FEDRAMP_STAGING_HOST = 'as.staging.atl-paas-us-gov-mod.net/api/v1';
const FEDRAMP_PROD_HOST = 'as.atlassian-us-gov-mod.com/api/v1';
const STARGATE_FEDRAMP_STAGING_HOST = 'api.stg.atlassian-us-gov-mod.com/gasv3/api/v1';
const STARGATE_FEDRAMP_PROD_HOST = 'api.atlassian-us-gov-mod.com/gasv3/api/v1';
export const STARGATE_PROXY_PATH = '/gateway/api/gasv3/api/v1';

const handleFedRampModerate = ({
	useStargate,
	env,
	useLegacyUrl,
	envOverride,
}: {
	env: string;
	envOverride?: envOverrides;
	useLegacyUrl?: boolean;
	useStargate: boolean;
}): string => {
	if (env === envType.LOCAL || env === envType.DEV) {
		throw Error('No LOCAL or DEV environments for FedRAMP.');
	}

	if (env === envType.PROD && envOverride) {
		throw Error('Cannot override to FedRAMP Sandbox for Prod.');
	}

	if (!useStargate) {
		return env === envType.PROD ? FEDRAMP_PROD_HOST : FEDRAMP_STAGING_HOST;
	}

	if (useLegacyUrl) {
		return env === envType.PROD ? STARGATE_FEDRAMP_PROD_HOST : STARGATE_FEDRAMP_STAGING_HOST;
	}

	/**
	 * Stargate proxy path now needs to be the default to work around browser restrictions with cookies.
	 * For more details, see:
	 *  - https://experimentation-platform.atlassian.net/browse/MHD-326, and
	 *  - https://hello.atlassian.net/wiki/spaces/I/pages/1217206437/api-private+is+broken+is+your+product+impacted
	 */
	return window.location.host + STARGATE_PROXY_PATH;
};

const handleCommercial = ({
	useStargate,
	env,
	useLegacyUrl,
}: {
	env: string;
	useLegacyUrl?: boolean;
	useStargate: boolean;
}): string => {
	if (!useStargate) {
		return env === envType.PROD ? PROD_HOST : STAGING_HOST;
	}

	if (useLegacyUrl) {
		return env === envType.PROD ? STARGATE_PROD_HOST : STARGATE_STAGING_HOST;
	}

	/**
	 * Stargate proxy path now needs to be the default to work around browser restrictions with cookies.
	 * For more details, see:
	 *  - https://experimentation-platform.atlassian.net/browse/MHD-326, and
	 *  - https://hello.atlassian.net/wiki/spaces/I/pages/1217206437/api-private+is+broken+is+your+product+impacted
	 */
	return window.location.host + STARGATE_PROXY_PATH;
};

/**
 * Attempts to detect the Oasis Isolated Cloud (IC) environment using `@atlassian/atl-context`
 * When running inside an IC, every analytics event must carry the `ic` (isolation context id) field and events must be routed
 * through the IC-specific Stargate proxy rather than the regular GASv3 endpoints.
 *
 * @returns The isolation context id string when detected, or `undefined`.
 */
export function detectIsolationContext(): string | undefined {
	try {
		if (isIsolatedCloud()) {
			return isolationContextId();
		}
	} catch {
		// Silently ignore — atl-context may not be able to read cookies in
		// non-browser environments (e.g. SSR). Consumers can still set `ic`
		// explicitly via productInfo.
	}
	return undefined;
}

/**
 * Resolves the GASv3 API host based on environment, perimeter and IC context.
 *
 * When `ic` is present (either explicitly provided or auto-detected from `@atlassian/atl-context`), the function resolves the host to the IC-local
 * stargate proxy path (`<hostname>/gateway/api/gasv3/api/v1`), or returns the explicit `apiHost` when one is supplied by the caller.
 *
 * For non-IC environments the function delegates to the existing perimeter-specific handlers (Commercial / FedRAMP Moderate).
 */
export function selectHost({
	useStargate,
	env,
	useLegacyUrl,
	perimeter,
	ic,
	envOverride,
	apiHost,
}: {
	apiHost?: string;
	env: string;
	envOverride?: envOverrides;
	ic?: string;
	perimeter?: string;
	useLegacyUrl?: boolean;
	useStargate: boolean;
}): string {
	// When running inside an IC, events must be routed through the IC-specific Stargate proxy.
	if (ic) {
		if (useLegacyUrl) {
			throw Error('IC is set but useLegacyUrl is true.');
		}

        // If the caller has explicitly set an apiHost, use it as the endpoint.
		if (apiHost) {
			return apiHost;
		}

		// Auto-resolve: route through the IC-specific Stargate proxy.
		return (typeof window !== 'undefined' ? window.location.host : '') + STARGATE_PROXY_PATH;
	}

	// If the caller supplies an apiHost without IC, use it as-is.
	if (apiHost) {
		return apiHost;
	}

	if (perimeter === perimeterType.FEDRAMP_MODERATE) {
		return handleFedRampModerate({
			useStargate,
			env,
			useLegacyUrl,
			envOverride,
		});
	}

	return handleCommercial({ useStargate, env, useLegacyUrl });
}

export function isStargateProxyPath({ apiHost }: { apiHost: string }): boolean {
	return apiHost === window.location.host + STARGATE_PROXY_PATH;
}
